/**
 * Consent-gated Reddit pixel (#2447).
 *
 * Same grant as Google Analytics and the X pixel: `budgit_analytics_consent`
 * must be `granted`, and token-link pages never load it (#1216). Nothing here
 * creates `window.rdt` or inserts the script before that grant, and nothing
 * happens at all while `REACT_APP_REDDIT_PIXEL_ID` is unset.
 *
 * Events: PageVisit once per page boot after init, SignUp once per new web
 * account, Purchase once per Stripe checkout session with value and currency
 * only. No account id, email, or Checkout session id is sent to Reddit.
 */

import { getAnalyticsConsent, subscribeAnalyticsConsent } from './analyticsConsent';
import { isAnalyticsExcludedPath } from '../utils/sensitiveLinkToken';
import {
  getRedditPixelId,
  isRedditPixelConfigured,
  REDDIT_EVENTS,
  REDDIT_PIXEL_SCRIPT_SRC,
} from './redditPixelConfig';
import { markThirdPartyScriptInjected } from './reloadOnConsentWithdrawal';

const FIRED_STORAGE_PREFIX = 'reddit_pixel_fired:';

let pixelLoadStarted = false;
let pixelInitialized = false;
const firedKeys = new Set();
/** @type {Map<string, () => boolean>} */
const pendingUntilConsent = new Map();
let consentListenerInstalled = false;

function pathname() {
  if (typeof window === 'undefined' || !window.location) return '';
  return window.location.pathname || '';
}

function consentGranted() {
  return getAnalyticsConsent() === 'granted';
}

export function redditPixelMayLoad() {
  if (typeof window === 'undefined') return false;
  if (!isRedditPixelConfigured()) return false;
  if (!consentGranted()) return false;
  if (isAnalyticsExcludedPath(pathname())) return false;
  return true;
}

/** Reddit's documented stub: queue calls until pixel.js attaches sendEvent. */
function installRdtStub() {
  if (typeof window === 'undefined' || typeof window.rdt === 'function') return;
  const rdt = function rdtStub(...args) {
    if (rdt.sendEvent) {
      rdt.sendEvent.apply(rdt, args);
    } else {
      rdt.callQueue.push(args);
    }
  };
  rdt.callQueue = [];
  window.rdt = rdt;
}

function hasFired(onceKey) {
  if (firedKeys.has(onceKey)) return true;
  try {
    if (window.sessionStorage && window.sessionStorage.getItem(FIRED_STORAGE_PREFIX + onceKey)) {
      firedKeys.add(onceKey);
      return true;
    }
  } catch (_) {
    // Private mode: the in-memory set is the only guard.
  }
  return false;
}

function markFired(onceKey) {
  firedKeys.add(onceKey);
  try {
    if (window.sessionStorage) {
      window.sessionStorage.setItem(FIRED_STORAGE_PREFIX + onceKey, '1');
    }
  } catch (_) {
    // Ignore quota / private mode.
  }
}

function cleanParams(params) {
  const out = {};
  if (!params) return out;
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (value !== undefined && value !== null && value !== '') out[key] = value;
  });
  return out;
}

/**
 * Load pixel.js, rdt('init', pixelId), then one PageVisit for this page boot.
 * Idempotent. Returns false, and does not touch rdt, until the pixel id is
 * configured and analytics consent is granted.
 */
export function enableRedditPixel() {
  if (!redditPixelMayLoad()) return false;
  installRdtStub();

  if (!pixelLoadStarted) {
    const existing = document.querySelector(`script[src="${REDDIT_PIXEL_SCRIPT_SRC}"]`);
    if (!existing) {
      const script = document.createElement('script');
      script.async = true;
      script.src = REDDIT_PIXEL_SCRIPT_SRC;
      document.head.appendChild(script);
    }
    pixelLoadStarted = true;
    markThirdPartyScriptInjected();
  }

  if (!pixelInitialized) {
    pixelInitialized = true;
    try {
      window.rdt('init', getRedditPixelId());
      window.rdt('track', REDDIT_EVENTS.PAGE_VISIT);
    } catch (_) {
      // pixel.js may throw once attached; the page must not notice.
    }
  }
  return true;
}

export function bootRedditPixelFromConsent() {
  if (consentGranted()) {
    enableRedditPixel();
    flushRedditEventsAfterConsent();
  }
}

/**
 * rdt('track', eventName, params). No-ops before consent (no rdt call).
 * `onceKey` makes a repeated trigger a no-op for the rest of the tab session.
 */
export function trackRedditEvent(eventName, params, { onceKey } = {}) {
  if (!eventName || typeof window === 'undefined') return false;
  if (onceKey && hasFired(onceKey)) return false;
  if (!enableRedditPixel()) return false;
  if (typeof window.rdt !== 'function') return false;
  if (onceKey) markFired(onceKey);
  const cleaned = cleanParams(params);
  try {
    if (Object.keys(cleaned).length > 0) {
      window.rdt('track', eventName, cleaned);
    } else {
      window.rdt('track', eventName);
    }
  } catch (_) {
    // pixel.js sendEvent may throw; measurement never surfaces to the caller.
    return false;
  }
  return true;
}

function queueUntilConsent(onceKey, fire) {
  if (!isRedditPixelConfigured()) return false;
  if (hasFired(onceKey)) return false;
  if (typeof window !== 'undefined' && isAnalyticsExcludedPath(pathname())) return false;
  if (!consentGranted()) {
    // No decision yet: hold the event until Accept. A reject drops it.
    if (getAnalyticsConsent() == null) pendingUntilConsent.set(onceKey, fire);
    return false;
  }
  return fire();
}

/** Fire events that happened before Accept, only after the grant. */
export function flushRedditEventsAfterConsent() {
  if (!redditPixelMayLoad() || pendingUntilConsent.size === 0) return;
  const jobs = Array.from(pendingUntilConsent.values());
  pendingUntilConsent.clear();
  jobs.forEach((fire) => {
    try {
      fire();
    } catch (_) {
      // One event must not block the rest.
    }
  });
}

function installConsentListener() {
  if (consentListenerInstalled || typeof window === 'undefined') return;
  consentListenerInstalled = true;
  subscribeAnalyticsConsent((choice) => {
    if (choice === 'granted') {
      enableRedditPixel();
      flushRedditEventsAfterConsent();
    } else {
      // Reject drops anything held before the decision; a later Accept in
      // the same tab must not replay it.
      pendingUntilConsent.clear();
    }
  });
}

installConsentListener();

/** New web account finished PIN setup. Returning logins do not call this. */
export function trackRedditSignUp() {
  return queueUntilConsent('signup', () => trackRedditEvent(
    REDDIT_EVENTS.SIGN_UP,
    undefined,
    { onceKey: 'signup' }
  ));
}

/**
 * Stripe verify-session succeeded. `amountTotalCents` is Stripe's
 * amount_total (smallest currency unit). Value and currency are omitted
 * when either is missing. `localDedupeKey` stays in this tab and is never
 * copied onto the Reddit payload.
 */
export function trackRedditPurchase({ amountTotalCents, currency, localDedupeKey } = {}) {
  const onceKey = `purchase:${localDedupeKey || 'once'}`;
  const params = {};
  // null / undefined / '' are the backend's explicit "missing" values;
  // Number() would turn them into 0 and send a zero-value purchase.
  const amountMissing = amountTotalCents === null || amountTotalCents === undefined || amountTotalCents === '';
  const cents = amountMissing ? Number.NaN : Number(amountTotalCents);
  const code = typeof currency === 'string' && currency ? currency.toUpperCase() : '';
  if (Number.isFinite(cents) && code) {
    params.value = cents / 100;
    params.currency = code;
  }
  return queueUntilConsent(onceKey, () => trackRedditEvent(
    REDDIT_EVENTS.PURCHASE,
    params,
    { onceKey }
  ));
}

export function resetRedditPixelForTests() {
  pixelLoadStarted = false;
  pixelInitialized = false;
  firedKeys.clear();
  pendingUntilConsent.clear();
  if (typeof window !== 'undefined') {
    delete window.rdt;
  }
}
